<template>
  <div>
    <HeadContents
      :disableDelete="!activeFilter"
      :disableEdit="!activeFilter"
      @click-action="actionHandler"
    />
    <CPanel>
      <Loading :loading="loading" />
      <ErrorBoundary
        :stateError="fetchReportFiltersError"
        :hasRetry="hasRetry"
        @retry="handleRetry"
      >
        <ReportFilter :activeFilter.sync="activeFilter"
      /></ErrorBoundary>
      <ErrorBoundary>
        <FilterDetails :activeFilterId="activeFilter" />
      </ErrorBoundary>
    </CPanel>
    <div class="row reverse q-px-lg q-pb-xl q-mt-lg inline full-width">
      <span class="text-right">
        <CButton
          color="primary"
          class="q-mt-lg"
          :disable="!isAnyFilterSelected || reportsLoading"
          :loading="reportsLoading"
          @click="handleShowResults"
        >
          {{ $t('Show Results') }}
        </CButton>
        <HelpPopover placement="bottom">
          <p>
            {{
              $t(
                'Please note that the results shown in the "Preview" and "Show results" sections are based on the current month.'
              )
            }}
          </p>
        </HelpPopover>
      </span>
    </div>
    <div class="q-px-lg">
      <div class="result-horizontal-line" />
      <!-- Amount -->
      <div class="__BillingReport_Header_Content_TotalAmount">
        <CLabelValue :label="$t('Monthly total cost')" align="right" small-label>
          <div class="text-lg">
            <CCurrency
              :value="totalAmount"
              :type="currencyType()"
              :loading="reportsLoading"
              :tooltip="`${currencySymbol(currencyType())}${totalAmount}`"
            />
          </div>
        </CLabelValue>
      </div>
      <BillingReportBody :data="serviceData" :loading="reportsLoading" />
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import { mapActions, mapState } from 'vuex';
import { returnServices } from '@/lib/reports';
import CPanel from '@/components/uikit/CPanel';
import CTitle from '@/components/uikit/CTitle';
import CIcon from '@/components/uikit/CIcon';
import CSeparator from '@/components/uikit/CSeparator';
import CButton from '@/components/uikit/CButton';
import CCurrency from '@/components/uikit/CCurrency';
import CLabelValue from '@/components/uikit/CLabelValue';
import HelpPopover from '@/components/uikit/HelpPopover';
import BillingReportBody from '@/components/BillingReport/BillingReportBody';
import ReportFilter from './ReportFilter';
import HeadContents from './HeadContents';
import FilterDetails from './FilterDetails';
import Loading from '@/components/uikit/Loading';

import { analyzer } from '@/analyzer';

export default Vue.extend({
  name: 'ReportFilters',
  components: {
    CPanel,
    FilterDetails,
    CTitle,
    CIcon,
    CSeparator,
    HeadContents,
    ReportFilter,
    Loading,
    CCurrency,
    CLabelValue,
    BillingReportBody,
    CButton,
    HelpPopover,
  },
  data: () => ({
    activeFilter: null,
    vendor: 'aws',
    loading: false,
    serviceData: [],
  }),
  methods: {
    ...mapActions('reportFilters', {
      fetchReportFilters: 'fetchReportFilters',
      deleteFilterGroup: 'deleteFilterGroup',
      fetchReports: 'fetchReports',
    }),
    ...mapActions('actionsObserver', {
      setActionError: 'setActionError',
    }),
    handleShowResults() {
      let payload = {
        vendor: this.vendor,
        ...this.createFilterPayload,
      };
      if (payload && payload.awsOptions) {
        payload.awsOptions.groupByColumns =
          'account,serviceCode,region,instanceType,operation,resourceId';
      }
      this.fetchReports(payload);
    },
    handleFetch() {
      return this.fetchReportFilters({ vendor: this.vendor })
        .then(this.handleErrorReset)
        .catch(this.handleFetchError);
    },
    handleFetchError() {
      this.setActionError({
        actionPath: 'reportFilters/fetchReportFilters',
        status: true,
      });
    },
    handleErrorReset() {
      this.setActionError({
        actionPath: 'reportFilters/fetchReportFilters',
        status: false,
      });
    },
    handleRetry() {
      this.handleFetch();
    },
    setDefaultFilter() {
      if (this.$route.params.activeFilter) {
        this.activeFilter = this.$route.params.activeFilter;
        return;
      }
      const defaultIndex = this.$store.getters['reportFilters/getFiltersOptions']('aws').findIndex(
        i => i.label.toLowerCase() === 'default'
      );
      if (defaultIndex >= 0)
        this.activeFilter = this.$store.getters['reportFilters/getFiltersOptions']('aws')[
          defaultIndex
        ].value;
      else if (this.$store.getters['reportFilters/getFiltersOptions']('aws').length > 0)
        this.activeFilter = this.$store.getters['reportFilters/getFiltersOptions']('aws')[0].value;
    },
    actionHandler(action) {
      analyzer.track(`Action: SaveFilter Menu ${action} `);

      switch (action) {
        case 'create': {
          this.$router.push('/report_filters/create');
          break;
        }
        case 'edit': {
          this.$router.push(`/report_filters/edit/${this.activeFilter}`);
          break;
        }
        case 'delete': {
          this.$confirm(
            this.$t('Are you sure you want to delete this filter?'),
            this.$t('Delete'),
            {
              confirmButtonText: this.$t('Confirm'),
              cancelButtonText: this.$t('Cancel'),
              type: 'warning',
            }
          ).then(() => {
            this.loading = true;
            this.deleteFilterGroup({
              vendor: 'aws',
              filterId: this.activeFilter,
            })
              .then(() => {
                this.$message({
                  message: this.$t('Filter Deleted Successfully'),
                  type: 'success',
                });
                this.$router.push({
                  params: {
                    activeFilter: '',
                  },
                });
                this.handleFetch().then(() => {
                  this.setDefaultFilter();
                });
              })
              .catch(() => {
                this.$message.error('Error');
              })
              .finally(() => {
                this.loading = false;
              });
          });
          break;
        }
      }
    },
    currencyType() {
      return this.vendor === 'aws' || this.vendor === 'azure' ? 'usd' : 'jpy';
    },
    currencySymbol(currency) {
      switch (currency) {
        case 'usd':
          return '$';
        case 'jpy':
          return '¥';
        default:
          return '';
      }
    },
  },
  computed: {
    ...mapState('reportFilters', {
      reports: state => state.reports,
      reportsLoading: state => state.reportsLoading,
    }),
    allData() {
      const allData = this.reports;
      if (allData) {
        return allData;
      }
      return [];
    },
    totalAmount() {
      if (this.serviceData && this.serviceData.length > 0) {
        let total = 0;
        for (const svc of this.serviceData) {
          total += svc.total;
        }
        return total;
      }
      return 0;
    },
    fetchReportFiltersError() {
      return this.$store.getters['actionsObserver/getActionErrorStatus'](
        'reportFilters/fetchReportFilters'
      );
    },
    savedFilterOptions() {
      return this.$store.getters['reportFilters/getFiltersOptions'](this.vendor);
    },
    isAnyFilterSelected() {
      return this.activeFilter !== 'all';
    },
    createFilterPayload() {
      if (this.activeFilter === null) return null;
      if (!this.isAnyFilterSelected) return null;
      const data = this.$store.getters['reportFilters/getActiveFilterData']({
        vendor: 'aws',
        filterId: this.activeFilter,
      });
      return data ? data : null;
    },
    hasRetry() {
      return ![null, false].includes(this.fetchReportFiltersError);
    },
  },
  watch: {
    allData() {
      if (!this.allData) {
        return;
      }
      this.serviceData = returnServices(this.allData);
    },
  },
  beforeMount() {
    this.loading = true;
    this.handleFetch()
      .then(() => {
        this.setDefaultFilter();
      })
      .finally(() => {
        this.loading = false;
      });
  },
});
</script>
